import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../Components/SEO/SEO';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const BlogManage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    contentType: 'all',
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        navigate('/blog');
        return;
      }

      const data = await response.json();
      setSession(data);
    } catch (err) {
      console.error('Auth check failed:', err);
      navigate('/blog');
    }
  }, [navigate]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });

      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }

      if (filters.contentType !== 'all') {
        params.append('contentType', filters.contentType);
      }

      // For non-admins, only fetch their own posts
      const isAdmin = ['Core', 'Admin'].includes(session?.user?.role);
      if (!isAdmin) {
        params.append('author', session?.user?.id);
      }

      const response = await fetch(`${API_URL}/blog?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch posts');

      const data = await response.json();
      setPosts(data.posts);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
      setError(null);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters.contentType, filters.status, pagination.limit, pagination.page, session]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (session) {
      fetchPosts();
    }
  }, [fetchPosts, session]);

  const handleSearch = async () => {
    if (!filters.search.trim()) {
      fetchPosts();
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/blog/search?q=${encodeURIComponent(filters.search)}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setPosts(data.results);
      setError(null);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId, postTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${postTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/blog/${postId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete post');
      }

      // Refresh the list
      fetchPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handlePublish = async (postId) => {
    try {
      const response = await fetch(`${API_URL}/blog/${postId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          status: 'published',
          scheduledFor: null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to publish post');
      }

      fetchPosts();
    } catch (err) {
      console.error('Error publishing post:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const formatDate = (dateString, fallbackLabel = 'Not published') => {
    if (!dateString) return fallbackLabel;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPublishDateLabel = (post) => {
    if (post.status === 'scheduled') {
      return formatDate(post.scheduledFor, 'Scheduled');
    }
    return formatDate(post.publishedAt);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      published: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      archived: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status] || badges.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const isAdmin = ['Core', 'Admin'].includes(session?.user?.role);

  if (loading && !posts.length) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO title="Manage Blog Posts" description="Manage your blog posts" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Manage Blog Posts
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                {isAdmin ? 'Manage all blog posts' : 'Manage your blog posts'}
              </p>
            </div>
            <Link
              to="/blog/new"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                       font-medium transition-colors whitespace-nowrap"
            >
              + New Post
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSearch}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                           font-medium transition-colors"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Content Type Filter */}
            <div>
              <select
                value={filters.contentType}
                onChange={(e) => setFilters({ ...filters, contentType: e.target.value })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="blog">Blog Posts</option>
                <option value="article">Articles</option>
              </select>
            </div>

            {filters.search && (
              <button
                onClick={() => {
                  setFilters({ ...filters, search: '' });
                  fetchPosts();
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700
                         dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300
                         rounded-lg transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                        rounded-lg p-4 mb-8">
            <p className="text-red-800 dark:text-red-400">
              <strong>Error:</strong> {error}
            </p>
          </div>
        )}

        {/* Posts Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          {posts.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                No posts found. {filters.search && 'Try a different search term.'}
              </p>
              <Link
                to="/blog/new"
                className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700
                         text-white rounded-lg font-medium transition-colors"
              >
                Create Your First Post
              </Link>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300
                                   uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300
                                   uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300
                                   uppercase tracking-wider">
                        Status
                      </th>
                      {isAdmin && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300
                                     uppercase tracking-wider">
                          Author
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300
                                   uppercase tracking-wider">
                        Published
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300
                                   uppercase tracking-wider">
                        Views
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300
                                   uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {posts.map((post) => (
                      <tr key={post._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {post.title}
                              </div>
                              {post.categories && post.categories.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {post.categories.slice(0, 2).map((cat, idx) => (
                                    <span
                                      key={idx}
                                      className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900
                                               text-blue-800 dark:text-blue-200 rounded"
                                    >
                                      {cat}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                            {post.contentType || 'blog'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(post.status)}
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-gray-300">
                              {post.author?.name || 'Unknown'}
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {getPublishDateLabel(post)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {post.views || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Link
                              to={`/blog/${post.slug}`}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400
                                       dark:hover:text-blue-300"
                            >
                              View
                            </Link>
                            <Link
                              to={`/blog/${post._id}/edit`}
                              className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400
                                       dark:hover:text-indigo-300"
                            >
                              Edit
                            </Link>
                            {post.status !== 'published' && (
                              <button
                                onClick={() => handlePublish(post._id)}
                                className="text-green-600 hover:text-green-900 dark:text-green-400
                                         dark:hover:text-green-300"
                              >
                                Publish
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => handleDelete(post._id, post.title)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400
                                         dark:hover:text-red-300"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {posts.map((post) => (
                  <div key={post._id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {post.title}
                      </h3>
                      {getStatusBadge(post.status)}
                    </div>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {isAdmin && post.author?.name && (
                        <p>Author: {post.author.name}</p>
                      )}
                      <p className="capitalize">Type: {post.contentType || 'blog'}</p>
                      <p>Published: {getPublishDateLabel(post)}</p>
                      <p>Views: {post.views || 0}</p>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to={`/blog/${post.slug}`}
                        className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-800
                                 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                      >
                        View
                      </Link>
                      <Link
                        to={`/blog/${post._id}/edit`}
                        className="px-3 py-1 text-sm bg-indigo-100 dark:bg-indigo-900 text-indigo-800
                                 dark:text-indigo-200 rounded hover:bg-indigo-200 dark:hover:bg-indigo-800"
                      >
                        Edit
                      </Link>
                      {post.status !== 'published' && (
                        <button
                          onClick={() => handlePublish(post._id)}
                          className="px-3 py-1 text-sm bg-green-100 dark:bg-green-900 text-green-800
                                   dark:text-green-200 rounded hover:bg-green-200 dark:hover:bg-green-800"
                        >
                          Publish
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(post._id, post.title)}
                          className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900 text-red-800
                                   dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-800"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} posts
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300
                                 dark:border-gray-600 rounded-lg disabled:opacity-50
                                 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600
                                 transition-colors text-gray-700 dark:text-gray-300"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.pages}
                        className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300
                                 dark:border-gray-600 rounded-lg disabled:opacity-50
                                 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600
                                 transition-colors text-gray-700 dark:text-gray-300"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogManage;
